import { Link } from "react-router-dom";
import { ArrowRight, Clock, BookOpen, ChevronRight } from "lucide-react";
import CTASection from "@/components/CTASection";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import { ResourcesIllustration } from "@/components/illustrations/FintechIllustrations";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import { ARTICLES } from "@/data/articles.mjs";
export { ARTICLES };

export default function Resources() {
  return (
    <div data-testid="resources-page">
      <Seo {...getSeoForPath("/resources")} />
      {/* HERO — unified: text + illustration blended into one navy header */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)",
          }}
        />
        <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
        <div aria-hidden="true" className="absolute top-[30%] right-[2%] w-2 h-2 rounded-full bg-[var(--swipe-accent)] opacity-70 pointer-events-none" />
        <div aria-hidden="true" className="absolute top-[18%] left-[40%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-60 pointer-events-none" />
        <div aria-hidden="true" className="absolute bottom-[20%] left-[8%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-50 pointer-events-none" />

        <ParticleField
          density={36}
          connectDistance={130}
          className="absolute inset-0 w-full h-full"
        />

        <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
          <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">Blog</span>
          </nav>

          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-12 items-center">
            <Reveal>
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                  Knowledge Base
                </div>
                <h1 className="font-display-tight text-[2.75rem] sm:text-[3.5rem] lg:text-[4.25rem] leading-[1.04] font-extrabold text-white tracking-[-0.025em]">
                  <span className="text-[var(--swipe-accent)]">Blog</span>
                </h1>
                <p className="mt-6 text-[17px] text-white/75 leading-[1.7] max-w-xl">
                  Explore helpful insights, payment tips, POS guidance, and merchant processing
                  resources designed to help business owners make smarter decisions.
                </p>
                <div className="mt-7 flex flex-wrap gap-2.5">
                  {["Statements", "Pricing", "Switching", "Equipment"].map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center px-3.5 py-1.5 rounded-full bg-white/10 border border-white/15 text-[11px] font-bold uppercase tracking-wider"
                      style={{ color: "#FFFFFF" }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <ResourcesIllustration
                  transparent
                  className="relative w-full h-auto max-w-[560px] mx-auto"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section container-swipe">
        {ARTICLES.length > 0 && (
          <>
            {/* Featured article */}
            <Link
              to={`/resources/${ARTICLES[0].slug}`}
              className="bg-white border border-line rounded-2xl overflow-hidden hover-lift group block"
            >
              <div className="grid lg:grid-cols-[1.35fr,1fr]">
                <div className="relative h-64 sm:h-80 lg:h-auto lg:min-h-[360px] overflow-hidden">
                  <img
                    src={ARTICLES[0].image}
                    alt={ARTICLES[0].title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--swipe-navy)]/70 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-white" />
                </div>
                <div className="p-7 sm:p-9 flex flex-col justify-center">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--swipe-accent)]">
                    Featured · {ARTICLES[0].category}
                  </div>
                  <h3 className="mt-3 font-display-tight text-2xl sm:text-3xl font-black text-[var(--swipe-navy)] leading-tight">
                    {ARTICLES[0].title}
                  </h3>
                  <p className="mt-4 text-slate-600 leading-relaxed">{ARTICLES[0].excerpt}</p>
                  <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--swipe-navy)] group-hover:text-[var(--swipe-accent)]">
                    Read article <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </Link>

            {/* Remaining articles in a balanced 2-column row */}
            {ARTICLES.length > 1 && (
              <div className="mt-8 grid md:grid-cols-2 gap-6">
                {ARTICLES.slice(1).map((a) => (
                  <Link
                    key={a.slug}
                    to={`/resources/${a.slug}`}
                    className="bg-white border border-line rounded-2xl overflow-hidden hover-lift group block"
                  >
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={a.image}
                        alt={a.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-6">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--swipe-accent)]">
                        {a.category}
                      </div>
                      <h3 className="mt-2 font-display text-lg font-bold text-[var(--swipe-navy)] leading-snug">
                        {a.title}
                      </h3>
                      <p className="mt-2 text-sm text-slate-600 line-clamp-2">{a.excerpt}</p>
                      <div className="mt-4 flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                          <Clock className="w-3 h-3" /> {a.readTime}
                        </div>
                        <BookOpen className="w-4 h-4 text-slate-400 group-hover:text-[var(--swipe-accent)]" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <CTASection />
    </div>
  );
}
